// Componente Angular con un formulario para editar los datos de una solicitud de vacaciones.
// Se importa en vacaciones.module.ts

import { Component, Inject, OnInit } from "@angular/core";
import { MatDialogRef, MAT_DIALOG_DATA, MatDialog } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Vacaciones } from "../../../models/personal/vacaciones.model";
import { VacacionesComponent } from "./vacaciones.component";
import { VacacionesService } from "../../../services/employees/vacaciones.service";
import { EmployeesListModel } from "../employees-list.model";
import { UntypedFormControl, UntypedFormGroup, Validators } from "@angular/forms";

import moment from 'moment';

@Component({
    selector: "app-vacaciones-form",
    templateUrl: "./vacaciones-form.component.html",
    styleUrls: [],
    standalone: false
})
export class VacacionesFormComponent implements OnInit {
    // #region Propiedades del componente
    empleados: EmployeesListModel[] = [];
    form: UntypedFormGroup = null;
    vacaciones: Vacaciones = this.VacacionesService.newVacaciones();

    esEdicion: boolean = false;
    // #endregion

    constructor(
        public dialogRef: MatDialogRef<VacacionesComponent>,
        @Inject(MAT_DIALOG_DATA) public data: any,
        public dialog: MatDialog,
        protected snackBar: MatSnackBar,
        public VacacionesService: VacacionesService
    ) {}

    // #region Métodos del componente

    cancel(): void {
        this.dialogRef.close();
    }

    ngOnInit() {
        this.empleados = this.data.empleados;

        if (this.data.vacaciones != undefined && this.data.vacaciones.id > 0) {
            this.vacaciones = this.data.vacaciones;
            this.esEdicion = true;
        } else {
            this.vacaciones = this.VacacionesService.newVacaciones();
            this.esEdicion = false;
        }

        this.inicializarFormulario();
    }

    onSubmit() {
        if (this.form.valid) {
            if (this.esEdicion) {
                // Edición de vacaciones existentes
                this.vacaciones.employees_Id = this.form.value.employees_Id;
                this.vacaciones.inicio = this.form.value.inicio;
                this.vacaciones.fin = this.form.value.fin;
                this.vacaciones.motivo = this.form.value.motivo;

                this.VacacionesService.saveVacaciones(
                    this.vacaciones
                ).subscribe({
                    next: (resultado) => {
                        if (resultado) {
                            this.dialogRef.close();
                        }
                    },
                });
            } else {
                // Creación de nuevas vacaciones para múltiples empleados
                const employees_Ids = this.form.value.employees_Ids;
                if (employees_Ids && employees_Ids.length > 0) {
                    // Crear vacaciones para cada empleado seleccionado
                    let completedCount = 0;
                    const totalEmployees = employees_Ids.length;

                    employees_Ids.forEach((employeeId) => {
                        const nuevaVacacion = {
                            ...this.vacaciones,
                            employees_Id: employeeId,
                            inicio: this.form.value.inicio,
                            fin: this.form.value.fin,
                            motivo: this.form.value.motivo,
                            estado: 2,
                            fechaAprobacion: moment().toDate(),
                            fechaRechazo: null,
                            motivoRechazo: null,
                            fechaSolicitud: moment().toDate(),
                            users_id: 0,
                        };

                        this.VacacionesService.saveVacaciones(
                            nuevaVacacion
                        ).subscribe({
                            next: (resultado) => {
                                completedCount++;
                                if (completedCount === totalEmployees) {
                                    this.dialogRef.close();
                                }
                            },
                            error: (error) => {
                                console.error(
                                    "Error al crear vacaciones:",
                                    error
                                );
                                completedCount++;
                                if (completedCount === totalEmployees) {
                                    this.dialogRef.close();
                                }
                            },
                        });
                    });
                }
            }
        }
    }

    rechazar(): void {
        this.dialog
            .open(RechazarVacacionesComponent, {
                data: {
                    title: "¿Seguro que quiere RECHAZAR la solicitud de vacaciones?",
                },
            })
            .afterClosed()
            .subscribe((result: string) => {
                if (result.length > 0) {
                    // Rechazo de la solicitud de vacaciones seleccionada
                    this.vacaciones.estado = 3;
                    this.vacaciones.fechaAprobacion = null;
                    this.vacaciones.fechaRechazo = moment().toDate();
                    this.vacaciones.motivoRechazo = result;

                    this.VacacionesService.saveVacaciones(
                        this.vacaciones
                    ).subscribe({
                        next: (resultado) => {
                            if (resultado) {
                                this.snackBar.open(
                                    "Solicitud de vacaciones RECHAZADA",
                                    null,
                                    {
                                        duration: 3000,
                                    }
                                );

                                this.dialogRef.close();
                            }
                        },
                    });
                }
            });
    }

    validar(): void {
        this.dialog
            .open(ValidarVacacionesComponent, {
                data: {
                    title: "¿Seguro que quiere VALIDAR la solicitud de vacaciones?",
                },
            })
            .afterClosed()
            .subscribe((result: boolean) => {
                if (result) {
                    // Validación de las vacaciones seleccionadas
                    this.vacaciones.estado = 2;
                    this.vacaciones.fechaAprobacion = moment().toDate();
                    this.vacaciones.fechaRechazo = null;
                    this.vacaciones.motivoRechazo = null;

                    this.VacacionesService.saveVacaciones(
                        this.vacaciones
                    ).subscribe({
                        next: (resultado) => {
                            if (resultado) {
                                this.snackBar.open(
                                    "Solicitud de vacaciones VALIDADA",
                                    null,
                                    {
                                        duration: 3000,
                                    }
                                );

                                this.dialogRef.close();
                            }
                        },
                    });
                }
            });
    }

    // #endregion

    // #region Métodos privados del componente

    /**
     * Devuelve el nombre del empleado asociado a las vacaciones.
     * @param vacaciones Las vacaciones para obtener el nombre del empleado
     * @returns El nombre del empleado o cadena vacía si no se encuentra
     */
    getNombreEmpleado(vacaciones: Vacaciones): string {
        if (!vacaciones || !vacaciones.employees_Id) {
            return "";
        }
        const empleado = this.empleados.find(
            (e) => e.uid === vacaciones.employees_Id
        );
        return empleado ? empleado.name : "";
    }

    private inicializarFormulario(): void {
        let formGroupConfig: any;

        if (this.esEdicion) {
            // Para edición, usar employees_Id
            formGroupConfig = {
                employees_Id: new UntypedFormControl(
                    this.vacaciones.employees_Id,
                    Validators.required
                ),
                inicio: new UntypedFormControl(
                    this.vacaciones.inicio,
                    Validators.required
                ),
                fin: new UntypedFormControl(this.vacaciones.fin, Validators.required),
                motivo: new UntypedFormControl(
                    this.vacaciones.motivo,
                    Validators.required
                ),
            };
        } else {
            // Para creación, usar employees_Ids para selección múltiple
            formGroupConfig = {
                employees_Ids: new UntypedFormControl([], Validators.required),
                inicio: new UntypedFormControl(
                    this.vacaciones.inicio,
                    Validators.required
                ),
                fin: new UntypedFormControl(this.vacaciones.fin, Validators.required),
                motivo: new UntypedFormControl(
                    this.vacaciones.motivo,
                    Validators.required
                ),
            };
        }

        this.form = new UntypedFormGroup(formGroupConfig);
    }

    // #endregion
}

@Component({
    selector: "validar-vacaciones",
    templateUrl: "validar.component.html",
    standalone: false
})
export class ValidarVacacionesComponent implements OnInit {
    title: string = "";
    constructor(
        private dialogRef: MatDialogRef<ValidarVacacionesComponent>,
        @Inject(MAT_DIALOG_DATA) public data: any
    ) {
        this.title = this.data.title;
    }

    ngOnInit(): void {
        this.title = this.data.title;
    }

    rechazar(): void {
        this.dialogRef.close(false);
    }

    validar(): void {
        this.dialogRef.close(true);
    }
}

@Component({
    selector: "rechazar-vacaciones",
    templateUrl: "rechazar.component.html",
    standalone: false
})
export class RechazarVacacionesComponent implements OnInit {
    form: UntypedFormGroup = null;
    title: string = "";
    constructor(
        private dialogRef: MatDialogRef<RechazarVacacionesComponent>,
        @Inject(MAT_DIALOG_DATA) public data: any
    ) {
        this.title = this.data.title;

        this.form = new UntypedFormGroup({
            rechazo: new UntypedFormControl("", Validators.required),
        });
    }

    ngOnInit(): void {
        this.title = this.data.title;
    }

    rechazar(): void {
        if (this.form.valid) {
            this.dialogRef.close(this.form.value.rechazo);
            this.form.reset();
        }
    }

    cancel(): void {
        this.dialogRef.close("");
    }
}
